/**
 * Single source of truth for fonts the Document Designer can faithfully
 * reproduce in BOTH the browser preview (FontFace) and the PDF export (jsPDF).
 * The browser loader and the PDF embedder both resolve fonts through
 * `resolveFont`, so the preview and the PDF can never diverge.
 *
 * Font data lives in ./font-registry.data; this module holds only behaviour.
 */
import {
  fontRegistry,
  FontSource,
  FontWeight,
} from "./font-registry.data";

export { fontRegistry };
export type { FontSource, FontWeight };

const FALLBACK_FAMILY = "Arial";
const FALLBACK_WEIGHT: FontWeight = "normal";

/** Normalizes the loosely-typed `fontWeight` (string | number) to "normal" | "bold". */
export function normalizeWeight(weight: string | number): FontWeight {
  if (typeof weight === "number") {
    return weight >= 600 ? "bold" : "normal";
  }
  const normalized = weight.trim().toLowerCase();
  if (normalized === "bold" || normalized === "bolder") {
    return "bold";
  }
  const numericWeight = Number(normalized);
  if (!Number.isNaN(numericWeight)) {
    return numericWeight >= 600 ? "bold" : "normal";
  }
  return "normal";
}

const warnedFonts = new Set<string>();

/**
 * Resolves a (family, weight) to a bundled font, shared by the browser loader
 * and the PDF embedder. Falls back to the other weight of the family, then Arial.
 */
export function resolveFont(family: string, weight: string | number): FontSource {
  const normalizedWeight = normalizeWeight(weight);
  const familyEntry = fontRegistry[family];

  const exactMatch = familyEntry?.[normalizedWeight];
  if (exactMatch) {
    return exactMatch;
  }

  const otherWeight: FontWeight = normalizedWeight === "bold" ? "normal" : "bold";
  const otherWeightMatch = familyEntry?.[otherWeight];
  if (otherWeightMatch) {
    return otherWeightMatch;
  }

  const warnKey = `${family}__${normalizedWeight}`;
  if (!warnedFonts.has(warnKey)) {
    warnedFonts.add(warnKey);
    console.warn("[fonts] unsupported font, falling back to Arial", {
      family,
      weight,
    });
  }

  // Arial/normal is always present in the registry.
  return fontRegistry[FALLBACK_FAMILY][FALLBACK_WEIGHT] as FontSource;
}

/** Families a user may pick (internal fonts such as the signature font excluded). */
export const selectableFamilies: string[] = Object.entries(fontRegistry)
  .filter(([, weights]) =>
    Object.values(weights).some((source) => source?.selectable !== false),
  )
  .map(([family]) => family)
  .sort((left, right) => left.localeCompare(right));

/**
 * Weights worth offering for a family: "normal" plus "bold" only when bold is a
 * genuinely different file (avoids a fictitious toggle that renders identically).
 */
export function availableWeights(family: string): FontWeight[] {
  const familyEntry = fontRegistry[family];
  if (!familyEntry) {
    return [];
  }
  const normalSource = familyEntry.normal;
  const boldSource = familyEntry.bold;
  const weights: FontWeight[] = [];
  if (normalSource) {
    weights.push("normal");
  }
  // Offer "bold" only as a genuinely different file. This also covers a
  // bold-only family: with no normal source the paths differ, so bold is added.
  if (boldSource && boldSource.modulePath !== normalSource?.modulePath) {
    weights.push("bold");
  }
  return weights;
}
